import { AST, From, Relation, SQL, Ty, EraCache } from '@cotera/era';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { HandlerOpts } from './handlers';
import { CoteraDuckDB } from '../db';
import range from 'lodash/range';
import { err, ok } from 'neverthrow';
import { CancelablePromise } from '../cancelable';
import type { CoteraApiV2 } from '../api/typed-client';
import { config } from '@cotera/client/config/config';
import { Assert, assertOk } from '@cotera/utilities';
import mapValues from 'lodash/mapValues';
import { DateTime } from 'luxon';
import {
  useAppData,
  useTenantedClient,
  useWhoami,
} from '@cotera/client/app/stores/org';
import { useGetAccessToken } from '@cotera/client/app/hooks/use-access-token';
import { useSuspenseQuery } from '@tanstack/react-query';
import { DataProvider } from './fn.type';
import { useEraScopesAwareRelIR } from '@cotera/client/app/pages/apps/compiler/macro-expansion/scopes';

const POLL_INTERVAL_MS = 1000;
const MAX_POLL_ATTEMPTS = 15 * 60;
const MAX_CACHE_TIME_IN_HOURS = 24;

// Simple cache for getUsableArtifactCache
type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

type SimpleCache<T> = {
  [key: string]: CacheEntry<T>;
};

const artifactCache: SimpleCache<ExistingCache> = {};

const createCacheKey = (orgId: string, freshnessMs?: number): string => {
  return freshnessMs !== undefined ? `${orgId}:${freshnessMs}` : orgId;
};

const isCacheFresh = (
  entry: CacheEntry<ExistingCache>,
  freshnessMs: number
): boolean => {
  const now = Date.now();
  return now - entry.timestamp < freshnessMs;
};

type Client = Pick<
  CoteraApiV2['t']['artifacts'],
  'create' | 'getRequestById' | 'usableCache'
>;

export type CacheResultData =
  | { t: 'values' }
  | { t: 'file'; artifactId: string; createdAt: Date; name: string };
export type CacheResult = EraCache.ServeFromCacheResult<
  CacheResultData,
  { message: string }
>;

type ExistingCache = EraCache.ExistingCache<
  CacheResultData,
  { message: string }
>;

const ERA_CACHE_POLICY: EraCache.CachePolicy = {
  artifactSize: EraCache.ARTIFACT_SIZE,
  isFresh: ({ artifactCreatedAt }): boolean => {
    return (
      Math.abs(
        DateTime.now().diff(DateTime.fromJSDate(artifactCreatedAt)).as('hours')
      ) < MAX_CACHE_TIME_IN_HOURS
    );
  },
};

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const artifactRegistry: Record<
  string,
  {
    baseRel: AST.RelFR;
    artifactId: string;
    createdAt: Date;
  }
> = {};

export const cachedReqToIR = async (
  {
    signature,
    artifactId,
  }: {
    signature: { [name: string]: Ty.ExtendedAttributeType };
    artifactId: string;
  },
  {
    db,
    orgId,
    getAccessToken,
  }: {
    db: CoteraDuckDB;
    orgId: string;
    getAccessToken?: () => Promise<string | undefined>;
  }
): Promise<{
  ir: AST.RelIR;
  name: string;
}> => {
  const fileName = `${artifactId}.parquet`;
  let url = `${config.apiUrl}/assets/artifacts/${orgId}/${artifactId}.parquet`;
  const accessToken = await getAccessToken?.();
  if (accessToken) {
    url += `?token=${encodeURIComponent(accessToken)}`;
  }
  await db.registerFileURL(fileName, url);
  const file = From({
    uri: fileName,
    attributes: signature,
  });

  return {
    ir: file.ir(),
    name: fileName,
  };
};

export const waitForArtifact =
  (
    queryClient: QueryClient,
    client: Client,
    initedDb: Promise<CoteraDuckDB>,
    orgId: string,
    getAccessToken?: () => Promise<string | undefined>
  ) =>
  async (
    props: { artifactId: string; orgId: string },
    opts: HandlerOpts
  ): Promise<CacheResult> => {
    const db = await initedDb;
    const { artifactId } = props;

    for (const _ of range(MAX_POLL_ATTEMPTS)) {
      if (opts?.abort?.signal?.aborted) {
        return err({ message: 'Aborted' });
      }

      const response = await new CancelablePromise(
        queryClient.fetchQuery({
          queryKey: queryKeys.artifacts.requestById({
            orgId,
            reqId: artifactId,
          }),
          queryFn: () => client.getRequestById({ id: artifactId }),
        }),
        {
          controller: opts?.abort,
        }
      );

      if (response.isOk() && response.value !== null) {
        const { fullfillment, failure } = response.value;
        if (fullfillment !== null) {
          const { ir, name } = await cachedReqToIR(
            {
              signature: fullfillment.signature as {
                [name: string]: Ty.ExtendedAttributeType;
              },
              artifactId,
            },
            { db, orgId, getAccessToken }
          );

          return ok({
            ir,
            meta: {
              t: 'file',
              name,
              artifactId,
              createdAt:
                fullfillment.createdAt instanceof Date
                  ? fullfillment.createdAt
                  : new Date(fullfillment.createdAt as string),
            },
          });
        }

        if (failure !== null) {
          return err({ message: failure.errorMessage });
        }
      } else if (response.isErr()) {
        return err({ message: response.error.err.errorType });
      }

      await sleep(POLL_INTERVAL_MS);
    }

    return err({
      message: `Poll Attempts Exceeded for Artifact "${artifactId}"`,
    });
  };

// Original implementation without caching
export const getUsableArtifactCacheUncached = async ({
  initedDb,
  api,
  queryClient,
  orgId,
  abort,
  getAccessToken,
}: {
  initedDb: Promise<CoteraDuckDB>;
  api: Pick<CoteraApiV2['t'], 'artifacts'>;
  queryClient: QueryClient;
  orgId: string;
  abort?: AbortController;
  getAccessToken?: () => Promise<string | undefined>;
}): Promise<ExistingCache> => {
  const db = await initedDb;

  const apiRes = await api.artifacts.usableCache({});

  const data = assertOk(apiRes);

  return mapValues(data, ({ id: artifactId, status, createdAt }) => {
    const createdAtDate =
      createdAt instanceof Date ? createdAt : new Date(createdAt as string);
    const fetch = async (): Promise<CacheResult> => {
      if (status.t === 'ready') {
        const { ir, name } = await cachedReqToIR(
          {
            artifactId,
            signature: status.signature as {
              [name: string]: Ty.ExtendedAttributeType;
            },
          },
          { db, orgId, getAccessToken }
        );
        return ok({
          ir,
          meta: {
            name,
            t: 'file',
            artifactId,
            createdAt: createdAtDate,
          },
        });
      }

      return waitForArtifact(
        queryClient,
        api.artifacts,
        initedDb,
        orgId,
        getAccessToken
      )({ artifactId, orgId }, { abort });
    };

    const complete = status.t === 'ready' ? status.complete : false;

    //TODO change this to check if values clause or file
    return {
      fetch,
      complete,
      meta: {
        artifactId,
        createdAt: createdAtDate,
        t: 'file' as const,
        name: '',
      },
      createdAt: createdAtDate,
    };
  }) as ExistingCache;
};

// Cached version with freshness parameter
export const getUsableArtifactCache = async ({
  initedDb,
  api,
  queryClient,
  orgId,
  abort,
  freshnessMs,
  getAccessToken,
}: {
  initedDb: Promise<CoteraDuckDB>;
  api: Pick<CoteraApiV2['t'], 'artifacts'>;
  queryClient: QueryClient;
  orgId: string;
  abort?: AbortController;
  freshnessMs?: number; // Optional freshness parameter in milliseconds
  getAccessToken?: () => Promise<string | undefined>;
}): Promise<ExistingCache> => {
  // If no freshness specified, use uncached version
  if (freshnessMs === undefined) {
    return getUsableArtifactCacheUncached({
      initedDb,
      api,
      queryClient,
      orgId,
      abort,
      getAccessToken,
    });
  }

  const cacheKey = createCacheKey(orgId, freshnessMs);
  const cachedEntry = artifactCache[cacheKey];

  // Check if we have a fresh cached entry
  if (cachedEntry && isCacheFresh(cachedEntry, freshnessMs)) {
    return cachedEntry.data;
  }

  // Fetch fresh data
  const freshData = await getUsableArtifactCacheUncached({
    initedDb,
    api,
    queryClient,
    orgId,
    abort,
    getAccessToken,
  });

  // Store in cache
  artifactCache[cacheKey] = {
    data: freshData,
    timestamp: Date.now(),
  };

  return freshData;
};

export const useUseableArtifactCache: DataProvider<
  { freshnessMs?: number },
  ExistingCache
> = (props, opts) => {
  const queryClient = useQueryClient();
  const api = useTenantedClient();
  const orgId = useWhoami((x) => x.org.id);
  const initedDb = useAppData((s) => s.initedDb);
  const getAccessToken = useGetAccessToken();

  return useSuspenseQuery({
    queryFn: async () => {
      return getUsableArtifactCache({
        initedDb,
        api,
        queryClient,
        orgId,
        abort: opts?.abort,
        freshnessMs: props.freshnessMs,
        getAccessToken,
      });
    },
    queryKey: queryKeys.artifacts.usableCache({
      orgId,
      freshnessMs: props.freshnessMs,
    }),
  });
};

export const getOrCreateArtifact = async ({
  rel,
  initedDb,
  existingCache,
  api,
  queryClient,
  orgId,
  opts,
  getAccessToken,
}: {
  rel: Relation;
  initedDb: Promise<CoteraDuckDB>;
  existingCache: ExistingCache;
  api: Pick<CoteraApiV2['t'], 'artifacts'>;
  queryClient: QueryClient;
  orgId: string;
  opts: HandlerOpts;
  getAccessToken?: () => Promise<string | undefined>;
}): Promise<CacheResult> => {
  const ir = rel.ir();

  const mode = Assert.assertOk(SQL.originForIR(ir));

  // Return early if it's a file that we can run in the FE
  // this handles early exit for values claues or files
  if (mode === 'file' || mode === null) {
    return ok({
      ir,
      meta: {
        t: 'values',
      },
    });
  }

  // Send the results to the BE
  const create = async (ir: AST.RelFR): Promise<CacheResult> => {
    const { id } = Assert.assertOk(await api.artifacts.create({ ast: ir }));

    const result = await new CancelablePromise(
      waitForArtifact(
        queryClient,
        api.artifacts,
        initedDb,
        orgId,
        getAccessToken
      )({ artifactId: id, orgId }, opts!),
      {
        controller: opts?.abort,
      }
    );

    if (result.isOk() && result.value.meta?.t === 'file') {
      artifactRegistry[result.value.meta!.name] = {
        baseRel: ir,
        ...result.value.meta,
      };
    }

    return result;
  };

  const res = await new CancelablePromise(
    EraCache.serve<CacheResultData, { message: string }>({
      ir,
      existingCache: existingCache,
      create,
      policy: ERA_CACHE_POLICY,
    }),
    { controller: opts?.abort }
  );

  return res;
};

export const useGetOrCreateArtifact: DataProvider<
  {
    rel: Relation;
    freshnessMs?: number;
  },
  CacheResult
> = (props, opts) => {
  const existingCache = useUseableArtifactCache(
    { freshnessMs: props.freshnessMs },
    opts
  );
  const rel = Relation.wrap(useEraScopesAwareRelIR(props.rel));
  const orgId = useWhoami((x) => x.org.id);
  const api = useTenantedClient();
  const queryClient = useQueryClient();
  const initedDb = useAppData((s) => s.initedDb);
  const getAccessToken = useGetAccessToken();

  return useSuspenseQuery({
    staleTime: Infinity, //we want to cache this for the duration of the users session, unless explictly invalidated
    queryFn: async () => {
      return getOrCreateArtifact({
        rel,
        initedDb,
        existingCache: existingCache.data,
        api,
        queryClient,
        orgId,
        opts: opts!,
        getAccessToken,
      });
    },
    queryKey: queryKeys.artifacts.forRel({ rel: props.rel, orgId }),
  });
};
